import type { ComponentType, SVGProps } from "react";
import type { Role } from "./role";

type IconProps = SVGProps<SVGSVGElement>;

function DashboardIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" {...props}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
    </svg>
  );
}

function ReportsIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M5 20V10M12 20V4M19 20v-7" />
    </svg>
  );
}

function ProfileIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

function CreditorIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M4 10l8-6 8 6" />
      <path d="M5 10v9M19 10v9M9 19v-6M15 19v-6" />
      <path d="M3 19h18" />
    </svg>
  );
}

function OnboardingIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 11v6M19 14h6" />
    </svg>
  );
}

// Shared by both ADMIN's role-upgrade queue and ENTERPRISE's join-enterprise
// queue — same "Membership" label everywhere, so they share one icon too
// (a person + checkmark, reading as "approve membership").
function MembershipIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M11 21H5a2 2 0 0 1-2-2v-2a4 4 0 0 1 4-4h4" />
      <circle cx="9" cy="7" r="4" />
      <path d="m16 19 2 2 4-4" />
    </svg>
  );
}

function PeopleIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function SetupIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M3 21v-6.5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2V21" />
      <path d="M13 21V10a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v11" />
      <path d="M3 21h18" />
      <path d="M13 8V4a1 1 0 0 1 1-1h2" />
    </svg>
  );
}

export interface NavItem {
  key: string;
  labelId: string;
  Icon: ComponentType<IconProps>;
  path: string;
}

// The full catalog of every nav item that exists anywhere in the sidebar —
// which of these a given role actually sees is decided by
// ROLE_NAV_ITEM_KEYS below, not by this array's order or presence alone.
export const DEFAULT_NAV_ITEMS: NavItem[] = [
  { key: "dashboard", labelId: "lender.sidebar.navDashboard", Icon: DashboardIcon, path: "/dashboard" },
  { key: "reports", labelId: "lender.sidebar.navReports", Icon: ReportsIcon, path: "/reports" },
  { key: "profile", labelId: "lender.sidebar.navProfile", Icon: ProfileIcon, path: "/profile" },
  { key: "creditor", labelId: "lender.sidebar.navCreditor", Icon: CreditorIcon, path: "/creditor" },
  { key: "onboarding", labelId: "lender.sidebar.navOnboarding", Icon: OnboardingIcon, path: "/onboarding" },
  { key: "members", labelId: "lender.sidebar.navMembers", Icon: MembershipIcon, path: "/members" },
  { key: "setup", labelId: "lender.sidebar.navSetup", Icon: SetupIcon, path: "/setup" },
  { key: "joinRequests", labelId: "lender.sidebar.navJoinRequests", Icon: MembershipIcon, path: "/join-requests" },
  { key: "people", labelId: "lender.sidebar.navPeople", Icon: PeopleIcon, path: "/people" },
];

// Single source of truth for "what can this role see" — both the sidebar
// (getNavItemsForRole) and the route guard (getRolesForNavKey, via
// RequireRole) read this same map, so extending access for a role, or
// adding a brand new role, is one array edit here, nothing else. A role
// missing from this map (a future role not yet wired up) resolves to no
// nav items rather than throwing.
//
// Order within each list is deliberate — most-used/primary task first,
// Profile always last (it's an account/settings page, not a task a role
// visits routinely):
// - AGENT's job is field onboarding, so Onboarding leads.
  // - LENDER/BORROWER: Dashboard is the entry point, then Reports.
  // - ADMIN: Dashboard first (overview), Members next (the admin's primary task —
  //   reviewing role-upgrade requests), Profile last (settings).
// - ENTERPRISE gets everything LENDER has, plus Creditor (managing their
//   own lenders/borrowers — the role's distinguishing capability, so it
//   leads right after Dashboard), People (lendcord-core's
//   PeopleDirectoryController — a read-only roster/funnel view of this
//   enterprise's borrowers/lenders, placed right after Creditor since both
//   are browsing/reporting views, ahead of the actionable queues below),
//   Join Requests (the approval inbox for
//   self-signed-up BORROWER/LENDER accounts asking to join this enterprise
//   — lendcord-core's EnterpriseController, an actionable queue so it sits
//   right after Creditor, mirroring how Members leads for ADMIN below),
//   Setup (branch + loan-plan configuration — lendcord-core's
//   BranchController, ENTERPRISE-only server-side too; placed after Join
//   Requests since branches feed both that approval flow's branch
//   reassignment and the Onboarding branch dropdown) and Onboarding
//   (bringing on Staff/Agents/Borrowers/Lenders, per lendcord-core's
//   OnboardingService allowed-targets matrix) ahead of Reports.
export const ROLE_NAV_ITEM_KEYS: Record<Role, string[]> = {
  AGENT: ["onboarding", "profile"],
  STAFF: ["onboarding", "profile"],
  LENDER: ["dashboard", "reports", "profile"],
  BORROWER: ["dashboard", "profile"],
  ENTERPRISE: ["dashboard", "creditor", "people", "joinRequests", "setup", "onboarding", "reports", "profile"],
  ADMIN: ["dashboard", "members", "profile"],
};

// Ordered per ROLE_NAV_ITEM_KEYS, not DEFAULT_NAV_ITEMS' catalog order — the
// per-role list order is deliberate (e.g. AGENT sees Onboarding first, its
// primary job, not Dashboard which it doesn't have access to at all).
export function getNavItemsForRole(role: Role | null): NavItem[] {
  const keys = (role && ROLE_NAV_ITEM_KEYS[role]) || [];
  const byKey = new Map(DEFAULT_NAV_ITEMS.map((item) => [item.key, item]));
  return keys.map((key) => byKey.get(key)).filter((item): item is NavItem => item !== undefined);
}

// Reverse lookup of ROLE_NAV_ITEM_KEYS — every role that has this nav key,
// used by RequireRole so the route guard never needs its own separate role
// list to keep in sync with the sidebar's.
export function getRolesForNavKey(key: string): Role[] {
  return (Object.keys(ROLE_NAV_ITEM_KEYS) as Role[]).filter((role) => ROLE_NAV_ITEM_KEYS[role].includes(key));
}

// The redirect target once a role is known: its own first nav item, or
// /auth if the role is unrecognized/has no items at all.
export function getDefaultPathForRole(role: Role | null): string {
  return getNavItemsForRole(role)[0]?.path ?? "/auth";
}
